import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { GET_PRODUCT_BY_ID, CREATE_REVIEW } from "../../utils/constant";
import API_CLIENT from "../../lib/api-client";
import { toast } from "react-hot-toast";
import { useCartStore } from "../../store/slice/cartSlice";
import { useWishlistStore } from "../../store/slice/wishlistSlice";
import { useAuthStore } from "../../store/slice/authSlice";
import { Rating, Tab, Tabs, CircularProgress } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHeart,
  faShoppingCart,
  faArrowLeft,
  faCircleCheck,
  faCircleXmark,
  faTruck,
  faShield,
  faRotateLeft,
  faMapMarkerAlt,
  faInfoCircle,
  faCartPlus,
  faStar,
  faImage,
  faShare,
  faUser,
  faTrashAlt,
} from "@fortawesome/free-solid-svg-icons";
import RelatedProduct from "../../component/RelatedProduct"; 

const ProductDetails = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedColor, setSelectedColor] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [tabValue, setTabValue] = useState(0);
  const [reviewForm, setReviewForm] = useState({ rating: 0, comment: "" });

  const { addToCart, cart } = useCartStore();
  const { addToWishlist, wishlist, removeFromWishlist } = useWishlistStore();
  const { isLoggedIn } = useAuthStore();
  const navigate = useNavigate();
  const imageRef = useRef(null);

  const isInCart = product
    ? cart.some((item) => item._id === product._id)
    : false;
  const isInWishlist = product
    ? wishlist.some((item) => item._id === product._id)
    : false;

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        const response = await API_CLIENT.get(`${GET_PRODUCT_BY_ID}${id}`);
        
        if (response.data.success) {
          setProduct(response.data.product);

          // Set default selected color if available
          if (
            response.data.product.variation &&
            response.data.product.variation[0] &&
            response.data.product.variation[0].color &&
            response.data.product.variation[0].color.length > 0
          ) {
            const availableColor =
              response.data.product.variation[0].color.find((c) => c.stock > 0);
            setSelectedColor(
              availableColor || response.data.product.variation[0].color[0]
            );
          }
        }
      } catch (error) {
        console.error("Error fetching product:", error);
        setError("Failed to load product details. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    if (id) {
    fetchProduct();
    }
  }, [id]);

  const totalStock =
    product?.variation[0]?.color.reduce((sum, color) => {
      return sum + (Number(color.stock) || 0);
    }, 0) || 0;

  const handleColorSelect = (color) => {
    setSelectedColor(color);

    // Enhanced color-image matching logic
    if (color.image) {
      // Try exact match first
      const exactMatchIndex = product.images.findIndex(
        (img) => img === color.image
      );
      if (exactMatchIndex >= 0) {
        setCurrentImageIndex(exactMatchIndex);
        return;
      }

      // Try partial match (in case URLs have different formats but contain color name)
      const colorName = color.name.toLowerCase();
      const partialMatchIndex = product.images.findIndex((img) =>
        img.toLowerCase().includes(colorName)
      );
      if (partialMatchIndex >= 0) {
        setCurrentImageIndex(partialMatchIndex);
        return;
      }
    }

    // If no match found, try to find image containing the color name
    const colorNameInImages = product.images.findIndex((img) =>
      img.toLowerCase().includes(color.name.toLowerCase())
    );
    if (colorNameInImages >= 0) {
      setCurrentImageIndex(colorNameInImages);
    }
  };

  const handleQuantityChange = (newQuantity) => {
    const maxStock = selectedColor ? selectedColor.stock : totalStock;
    if (newQuantity > 0 && newQuantity <= maxStock) {
      setQuantity(newQuantity);
    }
  };

  const handleAddToCart = () => {
    if (!selectedColor) {
      toast.error("Please select a color");
      return;
    }

    if (selectedColor.stock < quantity) {
      toast.error("Not enough stock available");
      return;
    }

    const productToAdd = {
      ...product,
      selectedColor: selectedColor.name,
      quantity,
    };

    addToCart(productToAdd);
    toast.success("Added to cart successfully");
  };

  const handleImageClick = (index) => {
    setCurrentImageIndex(index);
  };

  const handleReviewSubmit = async (e) => {
    e.preventDefault();

    if (!isLoggedIn) {
      toast.error("Please log in to submit a review");
      navigate("/login", { state: { from: `/product/${id}` } });
      return;
    }

    if (reviewForm.rating === 0) {
      toast.error("Please select a rating");
      return;
    }

    try {
      const response = await API_CLIENT.post(CREATE_REVIEW, {
        productId: id,
        rating: reviewForm.rating,
        comment: reviewForm.comment,
      });

      if (response.data.success) {
        toast.success("Review submitted successfully");
        setReviewForm({ rating: 0, comment: "" });

        // Refresh product to show updated reviews
        const updatedProduct = await API_CLIENT.get(
          `${GET_PRODUCT_BY_ID}${id}`
        );
        setProduct(updatedProduct.data.product);
      }
    } catch (error) {
      toast.error("Failed to submit review");
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <CircularProgress color="primary" />
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="text-center py-16 px-4">
        <FontAwesomeIcon
          icon={faCircleXmark}
          className="text-red-500 text-5xl mb-4"
        />
        <h2 className="text-2xl font-bold text-gray-800 mb-2">
          Product Not Found
        </h2>
        <p className="text-gray-600 mb-6">
          {error || "The requested product couldn't be loaded."}
        </p>
        <button
          onClick={() => navigate(-1)}
          className="px-6 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 transition-colors"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
          Go Back
        </button>
      </div>
    );
  }

  // Calculate discounted price
  const discountedPrice = product.price * (1 - product.discount / 100);

  // Format currency
  const formatPrice = (price) => {
    return `₹${price.toLocaleString("en-IN")}`;
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Product Section */}
      <div className="container mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 p-4 md:p-8">
            {/* Left Column - Image Gallery */}
            <div className="lg:col-span-2">
              <div className="relative aspect-square rounded-xl overflow-hidden mb-4 border border-gray-200">
                <img
                  ref={imageRef}
                  src={product.images[currentImageIndex]}
              alt={product.name}
                  className="w-full h-full object-cover object-center transition-transform duration-500 hover:scale-105"
                />

                {/* Redesigned New Arrival badge */}
                {product.newProduct && (
                  <div className="absolute top-0 left-0">
                    <div
                      className="bg-gradient-to-r from-teal-500 to-teal-600 text-white py-1.5 px-4 
                      shadow-md transform -skew-x-12"
                    >
                      <span className="block transform skew-x-12 font-medium text-xs">
                        NEW ARRIVAL
                      </span>
          </div>
        </div>
          )}
          
                {/* Discount badge remains the same */}
            {product.discount > 0 && (
                  <div className="absolute top-4 right-4 bg-red-500 text-white px-3 py-1 rounded-full text-xs font-semibold">
                    -{product.discount}% OFF
                  </div>
                )}

                {/* Wishlist button remains the same */}
                <button
                  className={`absolute bottom-4 right-4 w-10 h-10 rounded-full flex items-center justify-center
                    transition-colors duration-200 shadow-md
                    ${isInWishlist
                      ? "bg-red-500 hover:bg-red-600"
                      : "bg-teal-600 hover:bg-teal-700"
                    }`}
                  onClick={() => {
                    if (isInWishlist) {
                      removeFromWishlist(product._id);
                      toast.success(`${product.name} removed from wishlist`, {
                        style: {
                          borderRadius: '10px',
                          background: '#333',
                          color: '#fff',
                        },
                      });
                    } else {
                      addToWishlist(product);
                      toast.success(`${product.name} added to wishlist`);
                    }
                  }}
                  aria-label={isInWishlist ? "Remove from wishlist" : "Add to wishlist"}
                >
                  <FontAwesomeIcon
                    icon={isInWishlist ? faTrashAlt : faHeart}
                    className="text-white"
                  />
                </button>
          </div>

              {/* Color thumbnails - highlight active color's image */}
              <div className="grid grid-cols-5 gap-2">
                {product.images.map((image, index) => (
                  <div
                    key={index}
                    onClick={() => handleImageClick(index)}
                    className={`aspect-square rounded-md cursor-pointer overflow-hidden border-2 transition-all ${
                      currentImageIndex === index
                        ? "border-teal-500 shadow-md transform scale-105"
                        : "border-gray-200 hover:border-teal-300"
                    }`}
                  >
                    <img
                      src={image}
                      alt={`${product.name} - View ${index + 1}`}
                      className="w-full h-full object-cover object-center"
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Right Column - Product Info */}
            <div className="lg:col-span-3">
              {/* Header info */}
              <div className="border-b border-gray-100 pb-6 mb-6">
                <div className="flex justify-between items-start">
                  <div>
                    <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                      {product.name}
                    </h1>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="flex items-center">
                        <Rating
                          value={product.ratings}
                          precision={0.5}
                          readOnly
                          size="small"
                        />
                        <span className="ml-2 text-sm text-gray-500">
                          ({product.reviews?.length || 0} reviews)
                        </span>
                      </div>
                      <span className="text-gray-300">|</span>
                      <span className="text-sm text-gray-500">
                        {product.saleCount} sold
                      </span>
                    </div>
                  </div>
                  <div>
                    <button className="text-gray-400 hover:text-teal-600 transition-colors">
                      <FontAwesomeIcon icon={faShare} />
                    </button>
                  </div>
                </div>

                <div className="flex items-center gap-2 mb-4">
                  <div className="text-xs px-2 py-1 bg-gray-100 rounded-full text-gray-700">
                    {product.category.name}
                  </div>
                  <div className="text-xs flex items-center gap-1 text-gray-500">
                    <FontAwesomeIcon
                      icon={faMapMarkerAlt}
                      className="text-teal-600"
                    />
                    {product.city}, {product.state}
            </div>
          </div>

                <div className="flex items-baseline">
                  <span className="text-3xl font-bold text-teal-600 mr-3">
                    {formatPrice(discountedPrice)}
            </span>
                  {product.discount > 0 && (
                    <span className="text-lg text-gray-400 line-through">
                      {formatPrice(product.price)}
              </span>
            )}
          </div>
              </div>

              {/* Enhanced Color selection with indicators for which image is shown */}
              {product.variation &&
                product.variation[0] &&
                product.variation[0].color && (
                  <div className="mb-6">
                    <h3 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                      <span className="mr-2">Available Colors</span>
                      <span className="text-xs text-gray-500">
                        (Select to view color-specific images)
                      </span>
                    </h3>
                    <div className="flex flex-wrap gap-3">
                      {product.variation[0].color.map((color, index) => {
                        // Check if this color has a matching image
                        const hasImage =
                          color.image &&
                          product.images.some(
                            (img) =>
                              img === color.image ||
                              img
                                .toLowerCase()
                                .includes(color.name.toLowerCase())
                          );

                        return (
            <button
                            key={index}
                            onClick={() => handleColorSelect(color)}
                            className={`group relative w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                              selectedColor?.name === color.name
                                ? "border-teal-500 shadow-md"
                                : "border-gray-200"
                            }`}
                            disabled={color.stock === 0}
                          >
                            <div className="absolute inset-0 flex flex-col items-center justify-center">
                              <span className="text-xs font-medium group-hover:opacity-100 opacity-80 capitalize">
                                {color.name}
                              </span>
                              {color.stock === 0 && (
                                <span className="absolute inset-0 bg-white/70 flex items-center justify-center text-red-500 text-xs font-medium">
                                  Out of Stock
                                </span>
                              )}
                              {hasImage && (
                                <div className="absolute top-1 right-1 w-3 h-3 bg-teal-500 rounded-full flex items-center justify-center">
                                  <FontAwesomeIcon
                                    icon={faImage}
                                    className="text-white text-[8px]"
                                  />
                                </div>
                              )}
                              {selectedColor?.name === color.name && (
                                <div className="absolute bottom-1 right-1 w-4 h-4 bg-teal-500 rounded-full flex items-center justify-center">
                                  <FontAwesomeIcon
                                    icon={faCircleCheck}
                                    className="text-white text-xs"
                                  />
                                </div>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

              {/* Quantity selector */}
              <div className="mb-6">
                <h3 className="text-sm font-medium text-gray-900 mb-3">
                  Quantity
                </h3>
                <div className="flex items-center">
                  <button
                    onClick={() => handleQuantityChange(quantity - 1)}
                    className="w-10 h-10 rounded-l-lg border border-gray-300 flex items-center justify-center text-gray-600 hover:bg-gray-100 transition-colors"
                    disabled={quantity <= 1}
                  >
                    -
                  </button>
                  <input
                    type="number"
                    value={quantity}
                    onChange={(e) =>
                      handleQuantityChange(parseInt(e.target.value) || 1)
                    }
                    className="w-16 h-10 border-t border-b border-gray-300 text-center text-gray-700 focus:outline-none"
                    min="1"
                    max={selectedColor?.stock || totalStock}
                  />
                  <button
                    onClick={() => handleQuantityChange(quantity + 1)}
                    className="w-10 h-10 rounded-r-lg border border-gray-300 flex items-center justify-center text-gray-600 hover:bg-gray-100 transition-colors"
                    disabled={quantity >= (selectedColor?.stock || totalStock)}
                  >
                    +
                  </button>
                  <span className="ml-4 text-sm text-gray-500">
                    {selectedColor ? selectedColor.stock : totalStock} available
                  </span>
                </div>
              </div>

              {/* Store details */}
              <div className="mb-6 p-4 bg-gray-50 rounded-xl">
                <div className="flex items-center gap-3 mb-2">
                  <FontAwesomeIcon
                    icon={faInfoCircle}
                    className="text-teal-600"
                  />
                  <h3 className="font-medium">
                    Sold by: {product.manufacturer}
                  </h3>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <span>Product ID: {product.productId}</span>
                  <span>•</span>
                  <span>
                    {product.newProduct ? "New Arrival" : "Standard Stock"}
                  </span>
                </div>
              </div>

              {/* Action buttons */}
              <div className="flex flex-col sm:flex-row gap-4 mb-6">
                <button
                  onClick={handleAddToCart}
                  disabled={totalStock === 0}
                  className={`flex-1 py-3 px-6 rounded-xl font-medium text-white flex items-center justify-center gap-2 transition-colors ${
                    totalStock === 0
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-teal-600 hover:bg-teal-700"
                  }`}
                >
                  <FontAwesomeIcon icon={faCartPlus} />
                  {isInCart ? "Update Cart" : "Add to Cart"}
            </button>

                {/* Contact Button */}
                <button
                  onClick={() => navigate('/contact')}
                  className="flex-1 py-3 px-6 rounded-xl font-medium border border-teal-500 text-teal-600 hover:bg-teal-50 transition-colors flex items-center justify-center"
                >
                  Inquiry
            </button>
          </div>

              {/* Shipping info */}
              <div className="flex flex-wrap gap-6 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <FontAwesomeIcon icon={faTruck} className="text-teal-600" />
                  <span>Free shipping</span>
                </div>
                <div className="flex items-center gap-2">
                  <FontAwesomeIcon icon={faShield} className="text-teal-600" />
                  <span>Secure transaction</span>
                </div>
                <div className="flex items-center gap-2">
                  <FontAwesomeIcon
                    icon={faRotateLeft}
                    className="text-teal-600"
                  />
                  <span>7-day returns</span>
                </div>
          </div>
        </div>
      </div>

          {/* Redesigned Product details tabs */}
          <div className="border-t border-gray-100 mt-8">
            <Tabs
              value={tabValue}
              onChange={(e, newValue) => setTabValue(newValue)}
              variant="fullWidth"
              indicatorColor="primary"
              textColor="primary"
              className="px-8"
            >
              <Tab label="Description" />
              <Tab label="Specifications" />
              <Tab label="Reviews" />
            </Tabs>

            <div className="p-8">
              {/* Enhanced Description Tab */}
              {tabValue === 0 && (
                <div className="max-w-4xl mx-auto">
                  {/* Main product description in highlighted box */}
                  <div className="bg-gray-50 border-l-4 border-teal-500 p-6 rounded-lg mb-8">
                    <h3 className="text-xl font-semibold text-gray-800 mb-4">
                      Product Overview
                    </h3>
                    <p className="text-gray-700 leading-relaxed">
                      {product.description}
                    </p>
                  </div>

                  {/* Features with icons */}
                  <h3 className="text-xl font-semibold text-gray-800 mb-4">
                    Key Features
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 mb-8">
                    {product.subDescription.map((item, index) => (
                      <div key={index} className="flex items-start">
                        <div className="flex-shrink-0 h-6 w-6 rounded-full bg-teal-100 text-teal-600 flex items-center justify-center mr-3 mt-0.5">
                          <FontAwesomeIcon
                            icon={faCircleCheck}
                            className="text-sm"
                          />
                        </div>
                        <p className="text-gray-600">{item}</p>
                      </div>
                    ))}
                  </div>

                  {/* Product tags */}
                  <div className="flex flex-wrap gap-2 mt-6">
                    <span className="text-sm text-gray-500 mr-2">Tags:</span>
                    <span className="px-3 py-1 bg-gray-100 rounded-full text-xs text-gray-700">
                      {product.category.name}
                    </span>
                    <span className="px-3 py-1 bg-gray-100 rounded-full text-xs text-gray-700">
                      {product.manufacturer}
                    </span>
                    <span className="px-3 py-1 bg-gray-100 rounded-full text-xs text-gray-700">
                      {product.city}
                    </span>
                    {product.newProduct && (
                      <span className="px-3 py-1 bg-teal-100 text-teal-700 rounded-full text-xs">
                        New Arrival
                      </span>
                    )}
                  </div>
                </div>
              )}

              {tabValue === 1 && (
                <div className="overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <tbody className="divide-y divide-gray-200">
                      {Array.from(Object.entries(product.attributes || {})).map(
                        ([key, value], index) => (
                          <tr
                            key={index}
                            className={
                              index % 2 === 0 ? "bg-gray-50" : "bg-white"
                            }
                          >
                            <td className="px-6 py-4 text-sm font-medium text-gray-900 whitespace-nowrap">
                              {key}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-600 whitespace-nowrap">
                              {value}
                            </td>
                          </tr>
                        )
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {tabValue === 2 && (
                <div>
                  {/* Review form */}
                  {isLoggedIn && (
                    <div className="bg-gray-50 p-6 rounded-xl mb-8">
                      <h3 className="text-lg font-semibold mb-4">
                        Write a Review
                      </h3>
                      <form onSubmit={handleReviewSubmit}>
                        <div className="mb-4">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Rating
                          </label>
                          <Rating
                            name="review-rating"
                            value={reviewForm.rating}
                            onChange={(event, newValue) => {
                              setReviewForm({
                                ...reviewForm,
                                rating: newValue,
                              });
                            }}
                            precision={0.5}
                            size="large"
                          />
                        </div>
                        <div className="mb-4">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Comment
                          </label>
                          <textarea
                            value={reviewForm.comment}
                            onChange={(e) =>
                              setReviewForm({
                                ...reviewForm,
                                comment: e.target.value,
                              })
                            }
                            className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-colors"
                            rows="4"
                            placeholder="Share your experience with this product..."
                          ></textarea>
                        </div>
                        <button
                          type="submit"
                          className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
                        >
                          Submit Review
                        </button>
                      </form>
                    </div>
                  )}

                  {/* Review list */}
                  <h3 className="text-xl font-semibold mb-4">
                    Customer Reviews ({product.reviews?.length || 0})
                  </h3>

                  {product.reviews && product.reviews.length > 0 ? (
                    <div className="space-y-6">
                      {product.reviews.map((review, index) => (
                        <div
                          key={index}
                          className="border-b border-gray-100 pb-6 mb-6 last:border-0"
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-600">
                              <FontAwesomeIcon icon={faUser} />
                            </div>
                            <div>
                              <div className="font-medium">
                                {review.userId?.name || "Customer"}
                              </div>
                              <div className="text-xs text-gray-500">
                                {new Date(
                                  review.createdAt
                                ).toLocaleDateString()}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center mb-2">
                            <Rating
                              value={review.rating}
                              readOnly
                              size="small"
                              precision={0.5}
                            />
                          </div>
                          <p className="text-gray-700">{review.comment}</p>
                          {review.image && (
                            <div className="mt-3">
                              <img
                                src={review.image}
                                alt="Review"
                                className="w-24 h-24 object-cover rounded-md border border-gray-200"
                              />
                            </div>
                          )}
                        </div>
                ))}
              </div>
                  ) : (
                    <div className="text-center py-12 bg-gray-50 rounded-xl">
                      <FontAwesomeIcon
                        icon={faStar}
                        className="text-3xl text-gray-300 mb-3"
                      />
                      <p className="text-gray-600">
                        No reviews yet. Be the first to review this product!
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Related products */}
      {product.category && product.category.id && (
        <div className="mt-12">
          <RelatedProduct id={product.category.id} />
        </div>
      )}
    </div>
  );
};

export default ProductDetails;
